import React, {useState} from 'react';
import PropTypes from 'prop-types';
import _TabbedControls from './_TabbedControls.react';
import _Accordion from './_Accordion.react';
import {Sliders} from '@phosphor-icons/react';

/**
 * A container for organizing control elements that can be positioned either in
 * a sticky header with tabs or an accordion sidebar. Both are collapsible.
 *
 * WARNING: This is an alpha version of a component. Breaking changes may be
 * made without respecting semantic versioning. Use at your own risk.
 *
 * **Example Usage**
 * ```
 * ddk.ControlPanel(
 *     position="top",
 *     default_open=True,
 *     control_groups=[
 *         {
 *             "title": "Model Parameters",
 *             "children": [
 *                 dcc.Slider(
 *                     name="n", min=0, max=10, marks={0: "0", 5: "5", 10: "10"}, value=5
 *                 ),
 *             ],
 *         },
 *         {
 *             "title": "Plot Axes",
 *             "children": [
 *                 dcc.Dropdown(
 *                     name="x-axis",
 *                     placeholder="City",
 *                     options=[
 *                         {"label": "New York City", "value": "NYC"},
 *                         {"label": "Montreal", "value": "MTL"},
 *                     ],
 *                     value="MTL",
 *                 ),
 *             ],
 *         },
 *     ],
 * )
 * ```
 */
export default function _ControlPanel({
    position,
    control_groups,
    default_open,
    action_button,
}) {
    const [isOpen, setIsOpen] = useState(default_open);
    const classes = [
        'control-panel',
        `control-panel-${position}`,
        isOpen ? 'expanded' : 'collapsed',
    ];
    return (
        // if the control panel is closed, we only render the button
        <div className={classes.join(' ')}>
            <div className="control-panel-header">
                <div
                    role="button"
                    tabIndex="0"
                    className="control-panel-toggle"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <Sliders size={32} />
                </div>
                {isOpen && position === 'left' && (
                    <span style={{fontSize: '16px'}}>Controls</span>
                )}
            </div>
            {isOpen &&
                (position === 'top' ? (
                    <_TabbedControls control_groups={control_groups} action_button={action_button}/>
                ) : (
                    <_Accordion control_groups={control_groups} action_button={action_button}/>
                ))}
        </div>
    );
}

_ControlPanel.defaultProps = {
    position: 'top',
    default_open: true,
};

_ControlPanel.propTypes = {
    /**
     * The control elements to be positioned in the Wrapper horizontally or
     * vertically, grouped by section.
     */
    control_groups: PropTypes.arrayOf(
        PropTypes.shape({
            /**
             * The title of the tab or accordion section.
             */
            title: PropTypes.string.isRequired,
            /**
             * The description of the tab or accordion section.
             */
            description: PropTypes.string.isRequired,
            /**
             * The id of the tab or accordion section.
             */
            id: PropTypes.string.isRequired,
            /**
             * The control elements to be placed in the corresponding section.
             */
            children: PropTypes.arrayOf(PropTypes.node).isRequired,
        })
    ).isRequired,
    /**
     * Whether we want header controls (top) or sidebar controls (left)
     */
    position: PropTypes.oneOf(['top', 'left']),
    /**
     * Whether the sidebar is open by default
     */
    default_open: PropTypes.bool,
    /**
     * The action button to be placed in the control panel.
     */
    action_button: PropTypes.node,
};
